// ============================================
// pages/Dashboard.jsx - Scriptus Pharma Partner
// Dashboard Unificada de Dispensação Inteligente
// GLASSMORPHISM PREMIUM REFINADO - INTEGRAÇÃO TOTAL SNGPC
// 
// ✅ CORRIGIDO: Integração com estrutura companyData
// ✅ CORRIGIDO: Leitura de dados com fallback legado
// ✅ CORRIGIDO: Compatibilidade com Register.jsx e Configuracoes.jsx
// ✅ CORRIGIDO: Limite 50 dispensações/mês para Basic
// ✅ CORRIGIDO: Ilimitado para Partner
// ✅ CORRIGIDO: Contador de uso mensal INTEGRADO com backend
// ✅ CORRIGIDO: Usa dados do backend como fonte única de verdade
// ✅ CORRIGIDO: Validação permitida mesmo com limite atingido
// ✅ NOVO: Atualização imediata após dispensação (sem reload)
// ============================================

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  QrCode, History, FileText, Sparkles, Package, ChevronRight,
  Download, Shield, CheckCircle, AlertTriangle, Loader2, Search,
  User, Pill, Clock, X, Calendar, Hash, Stethoscope, RefreshCw,
  ArrowRight, ArrowLeft, Building2, Info, AlertCircle, Infinity
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import HeaderMenu from '../components/HeaderMenu';
import ModalHistoricoDispensacoes from '../components/ModalHistoricoDispensacoes';

// ============================================
// API Service
// ============================================
import { auth } from '../config/firebase';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.scriptusrxia.com.br';

// ✅ Limite mensal para plano Basic (constante de referência visual)
const LIMITE_BASIC_MENSAL = 50;

async function getAuthToken() {
  try {
    const user = auth.currentUser;
    if (user) return await user.getIdToken();
    return null;
  } catch (error) {
    console.error('Erro ao obter token:', error);
    return null;
  }
}

async function apiRequest(endpoint, options = {}) {
  const token = await getAuthToken();
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers
    }
  };
  const response = await fetch(`${API_BASE}${endpoint}`, config);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `Erro ${response.status}`);
  }
  return response.json();
}

// ============================================
// ✅ HELPER: Extrair dados da farmácia
// Compatível com Register.jsx e Configuracoes.jsx
// Prioriza companyData (nova estrutura) com fallback para raiz (legado)
// ============================================
function extrairDadosFarmacia(pharmacyData) {
  if (!pharmacyData) return null;
  
  const cd = pharmacyData.companyData || {};
  const addr = cd.address || pharmacyData.address || {};
  
  return {
    // Dados da empresa - prioriza companyData
    name: cd.name || pharmacyData.name || '',
    tradeName: cd.tradeName || pharmacyData.tradeName || '',
    cnpj: cd.cnpj || pharmacyData.cnpj || '',
    crf: cd.crf || pharmacyData.crf || '',
    phone: cd.phone || pharmacyData.phone || '',
    afe: cd.afe || pharmacyData.afe || '',
    
    // Endereço
    address: {
      street: addr.street || '',
      number: addr.number || '',
      complement: addr.complement || '',
      neighborhood: addr.neighborhood || '',
      city: addr.city || '',
      state: addr.state || '',
      zipCode: addr.zipCode || ''
    },
    
    // Metadados (somente leitura)
    _meta: {
      uid: pharmacyData.uid,
      email: pharmacyData.email,
      plano: pharmacyData.plano || 'basic',
      statusAssinatura: pharmacyData.statusAssinatura || 'ativa',
      criadoEm: pharmacyData.criadoEm
    }
  };
}

// ============================================
// Componente: GlassCard Refinado
// ============================================
function GlassCard({ children, className = '', variant = 'default', elevated = false }) {
  const variants = {
    default: 'glass-card-refined',
    elevated: 'glass-card-elevated-refined',
    highlight: 'glass-card-highlight',
    info: 'glass-card-info',
    success: 'glass-card-success',
    warning: 'glass-card-warning'
  };

  return (
    <div className={`${elevated ? variants.elevated : variants[variant]} rounded-2xl ${className}`}>
      {children}
    </div>
  );
}

// ============================================
// Componente: InputField Refinado
// ============================================
function InputField({ label, icon: Icon, hint, incomplete, className = '', ...props }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <label className="block text-[10px] sf-pro-text font-semibold text-slate-600 uppercase tracking-wider">
        {label}
      </label>
      <div className="relative group">
        {Icon && (
          <Icon className={`absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 transition-all duration-200 ${
            incomplete 
              ? 'text-amber-500' 
              : 'text-slate-400 group-focus-within:text-emerald-500 group-focus-within:scale-110'
          }`} />
        )}
        <input
          className={`input-glass-refined w-full text-sm ${Icon ? 'pl-10' : 'pl-3'} pr-3 ${
            incomplete ? 'border-amber-300 bg-amber-50/40 focus:border-amber-400' : ''
          }`}
          {...props}
        />
      </div>
      {hint && (
        <div className="text-slate-500 text-[10px] sf-pro-text mt-1 flex items-center gap-1">
          <div className="w-1 h-1 rounded-full bg-slate-300"></div>
          {hint}
        </div>
      )}
    </div>
  );
}

// ============================================
// Componente: StepIndicator Refinado
// ============================================
function StepIndicator({ currentStep, steps }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-6">
      {steps.map((step, index) => (
        <div key={step.id} className="flex items-center">
          <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold transition-all duration-300 ${
            currentStep === step.id 
              ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/40 scale-110' 
              : steps.findIndex(s => s.id === currentStep) > index
                ? 'bg-gradient-to-br from-emerald-100 to-emerald-200 text-emerald-700 border-2 border-emerald-300/50'
                : 'bg-gradient-to-br from-slate-100 to-slate-200 text-slate-500 border-2 border-slate-200'
          }`}>
            {steps.findIndex(s => s.id === currentStep) > index ? (
              <CheckCircle className="w-4 h-4" />
            ) : (
              index + 1
            )}
          </div>
          {index < steps.length - 1 && (
            <div className={`w-8 sm:w-12 h-0.5 mx-1.5 transition-all duration-500 ${
              steps.findIndex(s => s.id === currentStep) > index 
                ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' 
                : 'bg-gradient-to-r from-slate-200 to-slate-300'
            }`} />
          )}
        </div>
      ))}
    </div>
  );
}

// ============================================
// Componente: MedicamentoItemSNGPC Refinado
// ============================================
function MedicamentoItemSNGPC({ 
  medicamento, 
  index, 
  dadosSngpc, 
  onDadosChange, 
  readOnly = false 
}) {
  const qtdPrescrita = medicamento.quantity || medicamento.quantidade || '0';
  const numPrescrito = parseInt(String(qtdPrescrita).replace(/\D/g, '')) || 0;
  
  const campos = ['ean', 'batch', 'expirationDate', 'quantity'];
  const preenchidos = campos.filter(c => dadosSngpc[c] && String(dadosSngpc[c]).trim() !== '').length;
  const percentual = Math.round((preenchidos / campos.length) * 100);
  
  return (
    <div className="medicamento-card-refined group">
      <div className="medicamento-header-refined mb-4">
        <div className="flex items-start gap-3">
          <div className="medicamento-icon-refined">
            <Pill className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="sf-pro-text font-bold text-slate-900 text-sm leading-tight mb-1.5">
              {medicamento.name || medicamento.nome}
            </h4>
            <p className="sf-pro-text text-slate-600 text-xs leading-relaxed mb-2">
              {medicamento.instructions || medicamento.posologia || medicamento.dosage}
            </p>
            <div className="flex flex-wrap gap-1.5">
              <span className="medicamento-badge medicamento-badge-primary">
                <span className="font-medium">Prescrito:</span> {numPrescrito}
              </span>
              {medicamento.concentration && (
                <span className="medicamento-badge medicamento-badge-info">
                  {medicamento.concentration}
                </span>
              )}
              {!readOnly && (
                <span className={`medicamento-badge ${
                  percentual === 100 
                    ? 'medicamento-badge-success' 
                    : 'medicamento-badge-warning'
                }`}>
                  {percentual === 100 ? (
                    <>
                      <CheckCircle className="w-2.5 h-2.5" />
                      <span>Completo</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-2.5 h-2.5" />
                      <span>{percentual}% SNGPC</span>
                    </>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {!readOnly && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <InputField
            label="Código EAN"
            icon={Hash}
            placeholder="7891234567890"
            value={dadosSngpc.ean || ''}
            onChange={(e) => onDadosChange(index, 'ean', e.target.value.replace(/\D/g, ''))}
            incomplete={!dadosSngpc.ean}
            hint="Código de barras"
          />
          
          <InputField
            label="Lote"
            icon={Package}
            placeholder="L123456"
            value={dadosSngpc.batch || ''}
            onChange={(e) => onDadosChange(index, 'batch', e.target.value.toUpperCase())}
            incomplete={!dadosSngpc.batch}
            hint="Lote do fabricante"
          />
          
          <InputField
            label="Validade"
            icon={Calendar}
            type="date"
            value={dadosSngpc.expirationDate || ''}
            onChange={(e) => onDadosChange(index, 'expirationDate', e.target.value)}
            incomplete={!dadosSngpc.expirationDate}
          />
          
          <InputField
            label="Qtd. Dispensada"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder={String(numPrescrito)}
            value={dadosSngpc.quantity || ''}
            onChange={(e) => {
              const valor = e.target.value.replace(/\D/g, '');
              onDadosChange(index, 'quantity', valor);
            }}
            incomplete={!dadosSngpc.quantity}
            hint={`Prescrito: ${numPrescrito}`}
          />
        </div>
      )}

      {readOnly && (
        <div className="flex items-center justify-end">
          <span className="sf-pro-text text-xs text-slate-700 font-semibold px-3 py-1.5 rounded-lg bg-slate-100/80">
            Qtd: {numPrescrito}
          </span>
        </div>
      )}
    </div>
  );
}

// ============================================
// ✅ Componente: IndicadorCompletudeSNGPC Refinado
// CORRIGIDO: Usa dados extraídos corretamente
// ============================================
function IndicadorCompletudeSNGPC({ dadosSngpc, dadosFarmacia }) {
  const totalCamposMed = dadosSngpc.length * 4;
  let preenchidosMed = 0;
  dadosSngpc.forEach(item => {
    if (item.ean) preenchidosMed++;
    if (item.batch) preenchidosMed++;
    if (item.expirationDate) preenchidosMed++;
    if (item.quantity) preenchidosMed++;
  });
  const percentualMed = totalCamposMed > 0 ? Math.round((preenchidosMed / totalCamposMed) * 100) : 0;
  
  // ✅ CORRIGIDO: Verifica dados extraídos (já na estrutura correta)
  const farmaciaCompleta = dadosFarmacia?.cnpj && dadosFarmacia?.crf;
  const farmaciaComEndereco = dadosFarmacia?.address?.city && dadosFarmacia?.address?.state;
  const farmaciaComAfe = !!dadosFarmacia?.afe;
  
  return (
    <GlassCard variant="info" className="p-4 mb-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="status-icon-refined bg-gradient-to-br from-blue-500 to-blue-600">
            <FileText className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="sf-pro-text font-bold text-slate-800 text-sm">Dados SNGPC</h3>
            <p className="sf-pro-text text-slate-600 text-xs">Progresso do preenchimento</p>
          </div>
        </div>
        <div className={`status-badge-refined ${
          percentualMed === 100 ? 'status-badge-success' : 'status-badge-warning'
        }`}>
          {percentualMed}%
        </div>
      </div>
      
      <div className="progress-container-refined mb-3">
        <div 
          className={`progress-bar-refined ${
            percentualMed === 100 ? 'progress-bar-success' : 'progress-bar-warning'
          }`}
          style={{ width: `${percentualMed}%` }}
        />
      </div>
      
      <div className="flex flex-wrap gap-2">
        <div className={`status-item-refined ${
          farmaciaCompleta ? 'status-item-success' : 'status-item-warning'
        }`}>
          <Building2 className="w-3.5 h-3.5" />
          <span>Farmácia {farmaciaCompleta ? '✔' : '(completar cadastro)'}</span>
        </div>
        {farmaciaComEndereco && (
          <div className="status-item-refined status-item-success">
            <CheckCircle className="w-3.5 h-3.5" />
            <span>Endereço ✔</span>
          </div>
        )}
        {farmaciaComAfe && (
          <div className="status-item-refined status-item-success">
            <Shield className="w-3.5 h-3.5" />
            <span>AFE ✔</span>
          </div>
        )}
        {!farmaciaCompleta && (
          <Link 
            to="/configuracoes" 
            className="status-item-refined status-item-link"
          >
            <span>Completar cadastro →</span>
          </Link>
        )}
      </div>
    </GlassCard>
  );
}

// ============================================
// ✅ CORRIGIDO: Componente Contador de Uso Mensal
// Agora usa dados do backend (stats) como fonte de verdade
// ============================================
function ContadorUsoMensal({ stats, isPartner }) {
  // ✅ Usa valores do backend diretamente
  const dispensacoesEsteMes = stats.thisMonth || 0;
  const limite = stats.limit?.monthly || LIMITE_BASIC_MENSAL;
  const restantes = stats.remaining ?? (limite - dispensacoesEsteMes);
  const percentualUsado = stats.percentage ?? Math.min((dispensacoesEsteMes / limite) * 100, 100);
  const limiteProximo = stats.limiteProximo || false;
  const limiteAtingido = stats.limiteAtingido || false;
  
  return (
    <div className="fixed bottom-6 left-6 z-40">
      <div className={`contador-uso-refined ${
        isPartner 
          ? 'contador-uso-partner' 
          : limiteAtingido 
            ? 'contador-uso-limite' 
            : limiteProximo 
              ? 'contador-uso-alerta' 
              : 'contador-uso-normal'
      }`}>
        <div className="flex items-center gap-2.5">
          {isPartner ? (
            <>
              <div className="contador-icon-refined bg-gradient-to-br from-purple-500 to-pink-500">
                <Infinity className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <p className="sf-pro-text font-bold text-purple-900 text-xs">
                  Ilimitado
                </p>
                <p className="sf-pro-text text-purple-700 text-[10px]">
                  Plano Partner
                </p>
              </div>
            </>
          ) : (
            <>
              <div className={`contador-icon-refined ${
                limiteAtingido 
                  ? 'bg-gradient-to-br from-red-500 to-red-600' 
                  : limiteProximo 
                    ? 'bg-gradient-to-br from-amber-500 to-amber-600'
                    : 'bg-gradient-to-br from-emerald-500 to-emerald-600'
              }`}>
                <QrCode className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <p className={`sf-pro-text font-bold text-xs ${
                  limiteAtingido 
                    ? 'text-red-900' 
                    : limiteProximo 
                      ? 'text-amber-900'
                      : 'text-slate-900'
                }`}>
                  {dispensacoesEsteMes}/{limite}
                </p>
                <p className={`sf-pro-text text-[10px] ${
                  limiteAtingido 
                    ? 'text-red-700' 
                    : limiteProximo 
                      ? 'text-amber-700'
                      : 'text-slate-600'
                }`}>
                  {limiteAtingido ? 'Limite atingido' : 'este mês'}
                </p>
              </div>
              {/* Mini barra de progresso */}
              <div className="w-12 h-1.5 bg-slate-200 rounded-full overflow-hidden ml-1">
                <div 
                  className={`h-full rounded-full transition-all duration-500 ${
                    limiteAtingido 
                      ? 'bg-red-500' 
                      : limiteProximo 
                        ? 'bg-amber-500'
                        : 'bg-emerald-500'
                  }`}
                  style={{ width: `${percentualUsado}%` }}
                />
              </div>
            </>
          )}
        </div>
        
        {/* Link para upgrade se Basic */}
        {!isPartner && (
          <Link 
            to="/assinatura?upgrade=partner" 
            className="contador-upgrade-link"
          >
            <Sparkles className="w-3 h-3" />
            <span>{limiteAtingido ? 'Fazer upgrade' : 'Seja Partner'}</span>
          </Link>
        )}
      </div>
    </div>
  );
}

// ============================================
// Componente Principal: Dashboard
// ============================================
export default function Dashboard() {
  // ✅ ATUALIZADO: Adiciona isPartner do AuthContext
  const { user, pharmacy, isPremium, isPartner } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  
  // ✅ CORRIGIDO: Extrai dados da farmácia usando helper
  // Compatível com estrutura companyData e fallback legado
  const dadosFarmaciaExtraidos = useMemo(() => {
    return extrairDadosFarmacia(pharmacy);
  }, [pharmacy]);
  
  const [scrolled, setScrolled] = useState(false);
  const [tokenInput, setTokenInput] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [messageType, setMessageType] = useState('info');
  
  const [etapa, setEtapa] = useState('inicial');
  const [receitaValidada, setReceitaValidada] = useState(null);
  const [dadosSngpc, setDadosSngpc] = useState([]);
  const [processando, setProcessando] = useState(false);
  const [jaDispensada, setJaDispensada] = useState(false);
  const [dadosDispensacao, setDadosDispensacao] = useState(null);
  const [xmlGerado, setXmlGerado] = useState(null);

  // ✅ CORRIGIDO: State expandido para armazenar todos os dados do backend
  const [stats, setStats] = useState({ 
    thisMonth: 0, 
    total: 0, 
    limit: null,
    isPartner: false,
    limiteAtingido: false,
    limiteProximo: false,
    remaining: LIMITE_BASIC_MENSAL,
    percentage: 0,
    mesReferencia: ''
  });

  const [modalHistoricoAberto, setModalHistoricoAberto] = useState(false);
  const [dispensacoesRecentes, setDispensacoesRecentes] = useState([]);
  const [historicoLoading, setHistoricoLoading] = useState(false);
  const [historicoAcabou, setHistoricoAcabou] = useState(false);

  // ✅ CORRIGIDO: Usar valores do backend como fonte de verdade
  const limiteAtingido = useMemo(() => {
    if (isPartner) return false; // Partner é ilimitado
    return stats.limiteAtingido;
  }, [isPartner, stats.limiteAtingido]);

  const dispensacoesRestantes = useMemo(() => {
    if (isPartner) return Infinity;
    return stats.remaining;
  }, [isPartner, stats.remaining]);

  const limiteProximo = useMemo(() => {
    if (isPartner) return false;
    return stats.limiteProximo;
  }, [isPartner, stats.limiteProximo]);

  const etapasFluxo = [
    { id: 'inicial', label: 'Buscar' },
    { id: 'receita', label: 'Revisar' },
    { id: 'sngpc', label: 'SNGPC' },
    { id: 'concluido', label: 'Concluído' }
  ];

  // ✅ CORRIGIDO: loadStats mapeia corretamente todos os campos do backend
  const loadStats = useCallback(async () => {
    if (!user) return;
    try {
      const response = await apiRequest('/api/v1/dispensation/stats');
      if (response.success) {
        const s = response.stats;
        setStats({
          thisMonth: s.thisMonth || 0,
          total: s.total || 0,
          limit: s.limit || null,
          isPartner: s.isPartner || false,
          limiteAtingido: s.limiteAtingido || false,
          limiteProximo: s.limiteProximo || false,
          remaining: s.limit?.remaining ?? (s.isPartner ? Infinity : LIMITE_BASIC_MENSAL - (s.thisMonth || 0)),
          percentage: s.limit?.percentage || 0,
          mesReferencia: s.limit?.mesReferencia || ''
        });
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  }, [user]);

  const loadHistorico = useCallback(async (refresh = false) => {
    if (!user) return;
    setHistoricoLoading(true);
    try {
      const response = await apiRequest('/api/v1/dispensation/history?limit=20');
      if (response.success) {
        setDispensacoesRecentes(response.dispensations || []);
        setHistoricoAcabou((response.dispensations || []).length < 20);
      }
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    } finally {
      setHistoricoLoading(false);
    }
  }, [user]);

  const loadMaisHistorico = useCallback(async () => {
    if (!user || historicoLoading || historicoAcabou) return;
    setHistoricoLoading(true);
    try {
      const lastItem = dispensacoesRecentes[dispensacoesRecentes.length - 1];
      const cursor = lastItem?.dispensedAt || '';
      const response = await apiRequest(`/api/v1/dispensation/history?limit=20&cursor=${encodeURIComponent(cursor)}`);
      if (response.success) {
        const novas = response.dispensations || [];
        if (novas.length === 0) {
          setHistoricoAcabou(true);
        } else {
          setDispensacoesRecentes(prev => [...prev, ...novas]);
          setHistoricoAcabou(novas.length < 20);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar mais histórico:', error);
    } finally {
      setHistoricoLoading(false);
    }
  }, [user, historicoLoading, historicoAcabou, dispensacoesRecentes]);

  const abrirHistorico = useCallback(() => {
    setModalHistoricoAberto(true);
    loadHistorico();
  }, [loadHistorico]);

  const handleDownloadXmlHistorico = useCallback(async (prescriptionId) => {
    if (!prescriptionId) return;
    showMessage('Gerando XML...', 'info');
    try {
      const response = await apiRequest(`/api/v1/dispensation/xml/${encodeURIComponent(prescriptionId)}`);
      if (response.success && response.xml) {
        downloadXml(response.xml, prescriptionId);
        showMessage('XML baixado com sucesso!', 'success');
      } else {
        throw new Error('XML não disponível');
      }
    } catch (error) {
      console.error('Erro ao baixar XML:', error);
      showMessage('Erro ao baixar XML. Tente novamente.', 'error');
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 10);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (etapa === 'inicial' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [etapa]);

  const showMessage = useCallback((msg, type = 'info') => {
    setMensagem(msg);
    setMessageType(type);
    setTimeout(() => setMensagem(''), 5000);
  }, []);

  // ✅ CORRIGIDO: Validação PERMITIDA mesmo com limite atingido
  // Apenas bloqueia a dispensação posterior
  const validarReceita = async () => {
    const token = tokenInput.trim().toUpperCase();
    if (!token) {
      showMessage('Digite ou escaneie o código da receita', 'warning');
      return;
    }

    setEtapa('validando');
    setProcessando(true);

    try {
      const response = await apiRequest(`/api/v1/dispensation/validate/${encodeURIComponent(token)}`);
      
      if (!response.success) {
        throw new Error(response.error?.message || 'Erro ao validar receita');
      }

      const foiDispensada = response.status === 'dispensed' || response.warning?.code === 'already-dispensed';
      
      if (foiDispensada) {
        setJaDispensada(true);
        setDadosDispensacao({
          dispensedAt: response.warning?.dispensedAt || response.dispensationData?.timestamp,
          pharmacyId: response.warning?.pharmacyId || response.dispensationData?.pharmacyId,
          pharmacyName: response.dispensationData?.pharmacyName
        });
      } else {
        setJaDispensada(false);
        setDadosDispensacao(null);
      }

      setReceitaValidada(response);
      
      setDadosSngpc(
        (response.items || []).map(item => {
          const qtd = item.quantity || item.quantidade || '0';
          const numero = parseInt(String(qtd).replace(/\D/g, '')) || 0;
          return {
            ean: item.ean || item.codigoEan || item.barcode || '',
            batch: '',
            expirationDate: '',
            quantity: String(numero),
            concentration: item.concentration || item.concentracao || ''
          };
        })
      );
      
      setEtapa('receita');
      
      if (foiDispensada) {
        showMessage('Receita já dispensada anteriormente. Visualização apenas.', 'warning');
      } else if (limiteAtingido) {
        // ✅ NOVO: Aviso de limite atingido após validação bem-sucedida
        showMessage('Receita válida! Porém, limite mensal atingido. Faça upgrade para dispensar.', 'warning');
      } else {
        showMessage('Receita validada com sucesso!', 'success');
      }

    } catch (error) {
      console.error('Erro ao validar receita:', error);
      let errorMsg = 'Erro ao validar receita. Verifique o código.';
      if (error.message.includes('não encontrada') || error.message.includes('not found')) {
        errorMsg = 'Receita não encontrada. Verifique o código digitado.';
      } else if (error.message.includes('expirada') || error.message.includes('expired')) {
        errorMsg = 'Esta receita está expirada.';
      } else if (error.message.includes('cancelada') || error.message.includes('cancelled')) {
        errorMsg = 'Esta receita foi cancelada.';
      }
      showMessage(errorMsg, 'error');
      setEtapa('inicial');
    } finally {
      setProcessando(false);
    }
  };

  const handleDadosSngpcChange = (index, field, value) => {
    setDadosSngpc(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const avancarParaSngpc = () => {
    // ✅ CORRIGIDO: Verifica limite antes de avançar para SNGPC
    if (limiteAtingido) {
      showMessage('Limite mensal de dispensações atingido. Faça upgrade para Partner.', 'error');
      return;
    }
    setEtapa('sngpc');
  };

  const voltarParaRevisao = () => {
    setEtapa('receita');
  };

  const calcularCompletude = () => {
    if (dadosSngpc.length === 0) return { percentual: 0, completo: false, itensValidos: 0 };
    
    let itensValidos = 0;
    let camposPreenchidos = 0;
    const totalCampos = dadosSngpc.length * 4;
    
    dadosSngpc.forEach(item => {
      const qty = parseInt(item.quantity) || 0;
      if (qty > 0) itensValidos++;
      if (item.ean) camposPreenchidos++;
      if (item.batch) camposPreenchidos++;
      if (item.expirationDate) camposPreenchidos++;
      if (item.quantity) camposPreenchidos++;
    });
    
    return {
      percentual: Math.round((camposPreenchidos / totalCampos) * 100),
      completo: camposPreenchidos === totalCampos,
      itensValidos
    };
  };

  const realizarDispensacao = async () => {
    if (!receitaValidada) return;

    // ✅ CORRIGIDO: Verifica limite antes de dispensar
    if (limiteAtingido) {
      showMessage('Limite mensal de dispensações atingido. Faça upgrade para Partner.', 'error');
      return;
    }

    const { itensValidos } = calcularCompletude();
    
    if (itensValidos === 0) {
      showMessage('Informe a quantidade de pelo menos um medicamento', 'warning');
      return;
    }

    setEtapa('dispensando');
    setProcessando(true);

    try {
      const dispensedItems = receitaValidada.items.map((item, index) => ({
        productId: dadosSngpc[index].ean || item.ean || `item-${index}`,
        productName: item.name || item.nome || '',
        quantity: parseInt(dadosSngpc[index].quantity) || 0,
        batch: dadosSngpc[index].batch || '',
        expirationDate: dadosSngpc[index].expirationDate || '',
        concentration: dadosSngpc[index].concentration || item.concentration || '',
        price: 0
      })).filter(item => item.quantity > 0);

      const payload = {
        prescriptionId: receitaValidada.prescriptionId,
        patientName: receitaValidada.patient?.name || '',
        patientCpf: receitaValidada.patient?.cpf || '',
        patientAddress: receitaValidada.patient?.address || '',
        doctorName: receitaValidada.doctor?.name || '',
        doctorCrm: receitaValidada.doctor?.crm || '',
        doctorUf: receitaValidada.doctor?.ufCrm || receitaValidada.doctor?.state || '',
        prescriptionDate: receitaValidada.prescriptionDate || receitaValidada.issuedAt || '',
        dispensedItems
      };

      const response = await apiRequest('/api/v1/dispensation/dispense', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Erro ao dispensar');
      }

      if (response.sngpcXml) {
        setXmlGerado(response.sngpcXml);
        downloadXml(response.sngpcXml, receitaValidada.prescriptionId);
      }

      setEtapa('concluido');
      
      const { completo } = calcularCompletude();
      if (completo) {
        showMessage('Dispensação realizada! XML SNGPC completo gerado.', 'success');
      } else {
        showMessage('Dispensação realizada! XML gerado (alguns campos opcionais não preenchidos).', 'success');
      }
      
      // ✅ CORRIGIDO: Atualiza stats IMEDIATAMENTE usando response.dispensationLimit
      if (response.dispensationLimit) {
        setStats(prev => ({
          ...prev,
          thisMonth: response.dispensationLimit.used,
          remaining: response.dispensationLimit.remaining,
          percentage: response.dispensationLimit.percentage || 0,
          limiteAtingido: response.dispensationLimit.limiteAtingido || false,
          limiteProximo: response.dispensationLimit.limiteProximo || false
        }));
      } else {
        // Fallback: recarrega stats se não veio no response
        loadStats();
      }

    } catch (error) {
      console.error('Erro na dispensação:', error);
      let errorMsg = 'Erro ao processar dispensação.';
      if (error.message.includes('já foi dispensada') || error.message.includes('already-dispensed')) {
        errorMsg = 'Esta receita já foi dispensada por outra farmácia.';
      } else if (error.message.includes('Limite') || error.message.includes('limit')) {
        errorMsg = 'Limite mensal de dispensações atingido. Faça upgrade para Partner.';
        // ✅ Recarrega stats para sincronizar
        loadStats();
      }
      showMessage(errorMsg, 'error');
      setEtapa('sngpc');
    } finally {
      setProcessando(false);
    }
  };

  const downloadXml = (xmlContent, prescriptionId) => {
    try {
      const blob = new Blob([xmlContent], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SNGPC_${prescriptionId}_${Date.now()}.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar XML:', error);
    }
  };

  const handleDownloadXmlAtual = () => {
    if (xmlGerado && receitaValidada?.prescriptionId) {
      downloadXml(xmlGerado, receitaValidada.prescriptionId);
      showMessage('XML baixado com sucesso!', 'success');
    }
  };

  const novaDispensacao = () => {
    setEtapa('inicial');
    setTokenInput('');
    setReceitaValidada(null);
    setDadosSngpc([]);
    setJaDispensada(false);
    setDadosDispensacao(null);
    setXmlGerado(null);
  };

  const cancelar = () => {
    novaDispensacao();
  };

  return (
    <div className="min-h-screen bg-gradient-refined relative overflow-x-hidden">
      
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="background-pattern-refined"></div>
        <div className="background-glow-1"></div>
        <div className="background-glow-2"></div>
        <div className="background-glow-3"></div>
      </div>

      <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? 'header-scrolled-refined' : 'header-refined'
      }`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="logo-link-refined">
              <img src="/logo.PNG" alt="Scriptus RxIA" className="h-5 w-auto" />
            </Link>
            <HeaderMenu />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 sm:pt-24 pb-12 relative z-10">
        
        {/* ✅ Header com nome da farmácia */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-xl sm:text-2xl sf-pro-display font-medium text-gray-900 tracking-tight mb-1.5">
            Dispensação Inteligente
          </h1>
          <p className="text-xs sm:text-sm sf-pro-text text-gray-500 font-normal">
            {dadosFarmaciaExtraidos?.tradeName || dadosFarmaciaExtraidos?.name || 'Valide receitas digitais e gere XMLs SNGPC automaticamente'}
          </p>
        </div>

        {/* ✅ CORRIGIDO: Alerta de limite atingido para Basic */}
        {limiteAtingido && (
          <GlassCard variant="warning" className="p-4 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="status-icon-refined bg-gradient-to-br from-red-500 to-red-600">
                  <AlertTriangle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="sf-pro-text font-bold text-red-800 text-sm">Limite Mensal Atingido</h3>
                  <p className="sf-pro-text text-red-700 text-xs">
                    Você atingiu o limite de {stats.limit?.monthly || LIMITE_BASIC_MENSAL} dispensações do plano Basic. Você ainda pode validar receitas.
                  </p>
                </div>
              </div>
              <Link to="/assinatura?upgrade=partner" className="upgrade-button-refined text-xs py-2 px-4">
                <Sparkles className="w-3.5 h-3.5" />
                <span>Upgrade para Partner</span>
              </Link>
            </div>
          </GlassCard>
        )}

        <GlassCard 
          elevated={etapa !== 'inicial'} 
          className={`${etapa === 'inicial' ? 'p-4 sm:p-5 mb-5 shadow-xl' : 'p-5 sm:p-6 mb-6 shadow-2xl'}`}
        >
          
          <div className="relative mb-5">
            <div className={`flex flex-col ${etapa === 'inicial' ? 'sm:flex-row items-start sm:items-center justify-between gap-3' : 'lg:flex-row items-start lg:items-center justify-between gap-4'}`}>
              <div className="flex items-center space-x-2.5">
                <div className={`${etapa === 'inicial' ? 'icon-container-premium p-2 rounded-lg' : 'main-icon-refined'}`}>
                  <QrCode className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <h2 className={`${etapa === 'inicial' ? 'text-base sf-pro-display font-semibold text-gray-900' : 'card-title-refined'}`}>
                    {etapa === 'inicial' && 'Validar Receita Digital'}
                    {etapa === 'validando' && 'Validando Receita...'}
                    {etapa === 'receita' && 'Receita Validada'}
                    {etapa === 'sngpc' && 'Dados SNGPC'}
                    {etapa === 'dispensando' && 'Processando Dispensação...'}
                    {etapa === 'concluido' && 'Dispensação Concluída'}
                  </h2>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className={`${etapa === 'inicial' ? 'glass-badge-premium text-[10px] sf-pro-text font-medium' : 'badge-icp-refined'}`}>
                      <Shield className="w-2.5 h-2.5 mr-0.5" />
                      ICP-Brasil
                    </span>
                    {etapa !== 'inicial' && etapa !== 'validando' && etapa !== 'concluido' && (
                      <div className="badge-anvisa-refined">
                        <FileText className="w-3 h-3" />
                        <span>ANVISA</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <button
                onClick={abrirHistorico}
                className={`${etapa === 'inicial' ? 'button-glass-premium text-xs' : 'button-secondary-refined text-xs'}`}
              >
                <History className="w-3 h-3" />
                <span className="sf-pro-text">Ver Histórico</span>
              </button>
            </div>
          </div>

          {!['inicial', 'validando', 'dispensando'].includes(etapa) && !jaDispensada && (
            <StepIndicator currentStep={etapa} steps={etapasFluxo} />
          )}
          
          {etapa === 'inicial' && (
            <div className="space-y-3">
              <p className="sf-pro-text text-gray-500 text-xs">
                Digite o código da receita para validar a assinatura ICP-Brasil e iniciar a dispensação
              </p>

              <div className="space-y-3">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-xl blur-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-colors group-focus-within:text-emerald-500" />
                    <input
                      ref={inputRef}
                      type="text"
                      value={tokenInput}
                      onChange={(e) => setTokenInput(e.target.value.toUpperCase())}
                      onKeyDown={(e) => e.key === 'Enter' && validarReceita()}
                      placeholder="digite o código da receita (ex: a1b2c3d4...)"
                      className="input-glass-premium w-full pl-10 pr-3 py-3 text-center text-sm uppercase tracking-wider"
                      autoComplete="off"
                      spellCheck="false"
                    />
                  </div>
                </div>

                {/* ✅ CORRIGIDO: Botão de validar sempre habilitado (validação permitida mesmo com limite) */}
                <button
                  onClick={validarReceita}
                  disabled={!tokenInput.trim() || processando}
                  className={`button-primary-premium w-full py-3 text-xs ${
                    tokenInput.trim() && !processando
                      ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white' 
                      : 'bg-gray-200/80 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {processando ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="sf-pro-text font-semibold">Validando...</span>
                    </>
                  ) : (
                    <>
                      <Shield className="w-4 h-4" />
                      <span className="sf-pro-text font-semibold">Validar Assinatura ICP-Brasil</span>
                    </>
                  )}
                </button>
              </div>

              <div className="mt-4 pt-3 border-t border-gray-200/50">
                <div className="flex items-center justify-between gap-3">
                  {/* Validação Automática */}
                  <div className="flex items-start gap-2">
                    <div className="p-1 rounded-lg bg-emerald-100/80 mt-0.5">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="sf-pro-text font-semibold text-gray-900 text-xs">Validação Automática</p>
                      <p className="sf-pro-text text-gray-500 text-[10px]">Certificado ICP-Brasil verificado + XML SNGPC gerado automaticamente</p>
                    </div>
                  </div>
                  
                  {/* ✅ CORRIGIDO: CONTADOR DE USO MENSAL - usa dados do backend */}
                  <div className={`contador-uso-inline ${
                    isPartner 
                      ? 'contador-inline-partner' 
                      : limiteAtingido 
                        ? 'contador-inline-limite' 
                        : limiteProximo 
                          ? 'contador-inline-alerta' 
                          : 'contador-inline-normal'
                  }`}>
                    {isPartner ? (
                      <div className="flex items-center gap-2">
                        <div className="contador-inline-icon bg-gradient-to-br from-purple-500 to-pink-500">
                          <Infinity className="w-3 h-3 text-white" />
                        </div>
                        <div className="text-right">
                          <p className="sf-pro-text font-bold text-purple-900 text-xs leading-tight">Ilimitado</p>
                          <p className="sf-pro-text text-purple-600 text-[9px]">Partner</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className={`contador-inline-icon ${
                          limiteAtingido 
                            ? 'bg-gradient-to-br from-red-500 to-red-600' 
                            : limiteProximo 
                              ? 'bg-gradient-to-br from-amber-500 to-amber-600'
                              : 'bg-gradient-to-br from-emerald-500 to-emerald-600'
                        }`}>
                          <QrCode className="w-3 h-3 text-white" />
                        </div>
                        <div className="text-right">
                          <p className={`sf-pro-text font-bold text-xs leading-tight ${
                            limiteAtingido 
                              ? 'text-red-900' 
                              : limiteProximo 
                                ? 'text-amber-900'
                                : 'text-slate-900'
                          }`}>
                            {stats.thisMonth}/{stats.limit?.monthly || LIMITE_BASIC_MENSAL}
                          </p>
                          <p className={`sf-pro-text text-[9px] ${
                            limiteAtingido 
                              ? 'text-red-600' 
                              : limiteProximo 
                                ? 'text-amber-600'
                                : 'text-slate-500'
                          }`}>
                            {limiteAtingido ? 'Limite atingido' : 'este mês'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {etapa === 'validando' && (
            <div className="loading-container-refined">
              <div className="loading-animation-refined">
                <div className="loading-glow-refined"></div>
                <div className="loading-icon-refined">
                  <Loader2 className="w-7 h-7 text-emerald-600 animate-spin" />
                </div>
              </div>
              <div className="text-center">
                <h3 className="sf-pro-display text-slate-900 font-bold text-base">Validando assinatura digital...</h3>
                <p className="sf-pro-text text-slate-500 text-xs mt-1.5">Verificando certificado ICP-Brasil</p>
              </div>
            </div>
          )}

          {etapa === 'receita' && receitaValidada && (
            <div className="space-y-6">
              <GlassCard variant={jaDispensada ? 'warning' : 'success'} className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`status-icon-refined ${
                    jaDispensada 
                      ? 'bg-gradient-to-br from-amber-500 to-amber-600' 
                      : 'bg-gradient-to-br from-emerald-500 to-emerald-600'
                  }`}>
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className={`sf-pro-text font-bold text-base ${
                      jaDispensada ? 'text-amber-800' : 'text-emerald-800'
                    }`}>
                      {jaDispensada ? 'Receita Já Dispensada' : 'Assinatura Verificada'}
                    </h3>
                    <p className={`sf-pro-text text-xs ${
                      jaDispensada ? 'text-amber-700' : 'text-emerald-700'
                    }`}>
                      {jaDispensada 
                        ? `Dispensada em: ${dadosDispensacao?.dispensedAt ? new Date(dadosDispensacao.dispensedAt).toLocaleString('pt-BR') : 'N/A'}`
                        : `Certificado ICP-Brasil válido • ${receitaValidada.signature?.certificate?.issuer || 'ICP-Brasil'}`
                      }
                    </p>
                  </div>
                </div>
              </GlassCard>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <GlassCard variant="highlight" className="info-card-refined">
                  <div className="info-card-header-refined">
                    <div className="info-card-icon-refined bg-gradient-to-br from-blue-500 to-blue-600">
                      <Stethoscope className="w-4 h-4 text-white" />
                    </div>
                    <span className="info-card-label-refined">Prescritor</span>
                  </div>
                  <div className="info-card-content-refined">
                    <h4 className="info-card-title-refined sf-pro-display">{receitaValidada.doctor?.name}</h4>
                    <p className="info-card-subtitle-refined sf-pro-text">CRM: {receitaValidada.doctor?.crm}</p>
                    {receitaValidada.doctor?.specialty && (
                      <p className="info-card-extra-refined sf-pro-text">{receitaValidada.doctor.specialty}</p>
                    )}
                  </div>
                </GlassCard>
                
                <GlassCard variant="highlight" className="info-card-refined">
                  <div className="info-card-header-refined">
                    <div className="info-card-icon-refined bg-gradient-to-br from-purple-500 to-purple-600">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span className="info-card-label-refined">Paciente</span>
                  </div>
                  <div className="info-card-content-refined">
                    <h4 className="info-card-title-refined sf-pro-display">{receitaValidada.patient?.name}</h4>
                    <p className="info-card-subtitle-refined sf-pro-text">CPF: {receitaValidada.patient?.cpf}</p>
                  </div>
                </GlassCard>
              </div>

              <div>
                <div className="section-header-refined mb-4">
                  <div className="section-icon-refined">
                    <Pill className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="section-title-refined sf-pro-display">
                      Medicamentos Prescritos
                    </h3>
                    <p className="section-subtitle-refined sf-pro-text">
                      {receitaValidada.items?.length || 0} {receitaValidada.items?.length === 1 ? 'medicamento' : 'medicamentos'}
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  {(receitaValidada.items || []).map((med, index) => (
                    <MedicamentoItemSNGPC
                      key={index}
                      medicamento={med}
                      index={index}
                      dadosSngpc={dadosSngpc[index] || {}}
                      onDadosChange={handleDadosSngpcChange}
                      readOnly={true}
                    />
                  ))}
                </div>
              </div>

              {/* ✅ CORRIGIDO: Alerta de limite atingido na etapa de revisão */}
              {!jaDispensada && limiteAtingido && (
                <GlassCard variant="warning" className="p-3">
                  <div className="flex items-center justify-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    <span className="sf-pro-text text-red-800 text-xs font-medium">
                      Limite mensal atingido. Não é possível dispensar.
                    </span>
                    <Link to="/assinatura?upgrade=partner" className="text-red-800 underline text-xs font-semibold hover:text-red-900 transition-colors">
                      Faça upgrade
                    </Link>
                  </div>
                </GlassCard>
              )}

              {/* ✅ CORRIGIDO: Alerta de limite próximo */}
              {!jaDispensada && !limiteAtingido && limiteProximo && (
                <GlassCard variant="warning" className="p-3">
                  <div className="flex items-center justify-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <span className="sf-pro-text text-amber-800 text-xs font-medium">
                      Restam {dispensacoesRestantes} dispensações este mês.
                    </span>
                    <Link to="/assinatura?upgrade=partner" className="text-amber-800 underline text-xs font-semibold hover:text-amber-900 transition-colors">
                      Faça upgrade
                    </Link>
                  </div>
                </GlassCard>
              )}

              <div className="space-y-2.5 pt-4">
                {/* ✅ CORRIGIDO: Botão desabilitado se limite atingido */}
                {!jaDispensada && (
                  <button
                    onClick={avancarParaSngpc}
                    disabled={limiteAtingido}
                    className={`button-primary-refined w-full ${limiteAtingido ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <ArrowRight className="w-3.5 h-3.5" />
                    <span>{limiteAtingido ? 'Limite Atingido' : 'Preencher Dados SNGPC'}</span>
                  </button>
                )}
                
                <button 
                  onClick={cancelar} 
                  disabled={processando} 
                  className="button-secondary-refined w-full"
                >
                  <X className="w-3.5 h-3.5" />
                  <span>{jaDispensada ? 'Voltar' : 'Cancelar'}</span>
                </button>
              </div>
            </div>
          )}

          {etapa === 'sngpc' && receitaValidada && (
            <div className="space-y-6">
              {/* ✅ CORRIGIDO: Passa dados extraídos corretamente */}
              <IndicadorCompletudeSNGPC 
                dadosSngpc={dadosSngpc} 
                dadosFarmacia={dadosFarmaciaExtraidos} 
              />

              <GlassCard variant="info" className="p-4">
                <div className="flex items-start gap-3">
                  <div className="info-icon-refined">
                    <Info className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="sf-pro-display font-bold text-blue-900 text-sm mb-1.5">
                      Dados automáticos incluídos no XML
                    </h4>
                    <div className="space-y-1.5 text-xs">
                      <p className="sf-pro-text text-blue-800">
                        <span className="font-semibold">Da receita:</span> Prescritor, paciente, medicamentos
                      </p>
                      <p className="sf-pro-text text-blue-800">
                        <span className="font-semibold">Do seu cadastro:</span> {dadosFarmaciaExtraidos?.cnpj ? `CNPJ: ${dadosFarmaciaExtraidos.cnpj}` : 'CNPJ'}, {dadosFarmaciaExtraidos?.crf ? `CRF: ${dadosFarmaciaExtraidos.crf}` : 'CRF'}, AFE, endereço
                      </p>
                      <p className="sf-pro-text text-blue-700 font-medium">
                        Campos opcionais podem ficar em branco se indisponíveis.
                      </p>
                    </div>
                  </div>
                </div>
              </GlassCard>

              <div className="patient-summary-refined">
                <User className="w-4 h-4 text-slate-600" />
                <span className="sf-pro-text text-slate-700 font-medium text-xs">
                  <span className="font-bold">{receitaValidada.patient?.name}</span> • Receita #{receitaValidada.prescriptionId}
                </span>
              </div>

              <div className="space-y-4">
                {(receitaValidada.items || []).map((med, index) => (
                  <MedicamentoItemSNGPC
                    key={index}
                    medicamento={med}
                    index={index}
                    dadosSngpc={dadosSngpc[index] || {}}
                    onDadosChange={handleDadosSngpcChange}
                    readOnly={false}
                  />
                ))}
              </div>

              {calcularCompletude().percentual < 100 && (
                <GlassCard variant="warning" className="p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="sf-pro-display font-semibold text-amber-800 text-xs mb-0.5">Campos opcionais</h4>
                      <p className="sf-pro-text text-amber-700 text-xs leading-relaxed">
                        Você pode dispensar mesmo sem preencher todos os campos. 
                        O XML será gerado com os dados disponíveis. Recomendamos preencher EAN, lote e validade para compliance ANVISA completo.
                      </p>
                    </div>
                  </div>
                </GlassCard>
              )}

              <div className="space-y-2.5 pt-4">
                <button
                  onClick={realizarDispensacao}
                  disabled={processando}
                  className="button-primary-refined w-full"
                >
                  {processando ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Processando...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-3.5 h-3.5" />
                      <span>Dispensar & Gerar XML</span>
                    </>
                  )}
                </button>

                <button 
                  onClick={voltarParaRevisao} 
                  disabled={processando} 
                  className="button-secondary-refined w-full"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  <span>Voltar</span>
                </button>
              </div>
            </div>
          )}

          {etapa === 'dispensando' && (
            <div className="loading-container-refined">
              <div className="loading-animation-refined">
                <div className="loading-glow-refined bg-blue-400/20"></div>
                <div className="loading-icon-refined bg-gradient-to-br from-blue-500 to-blue-600">
                  <Loader2 className="w-7 h-7 text-white animate-spin" />
                </div>
              </div>
              <div className="text-center">
                <h3 className="sf-pro-display text-slate-900 font-bold text-base">Processando dispensação...</h3>
                <p className="sf-pro-text text-slate-500 text-xs mt-1.5">Gerando arquivo XML SNGPC</p>
              </div>
            </div>
          )}

          {etapa === 'concluido' && (
            <div className="success-container-refined">
              <div className="success-animation-refined">
                <div className="success-glow-refined"></div>
                <div className="success-icon-refined">
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
              </div>
              
              <div className="text-center mb-6">
                <h3 className="success-title-refined sf-pro-display">
                  Dispensação Concluída!
                </h3>
                <p className="success-subtitle-refined sf-pro-text">
                  XML SNGPC gerado e baixado automaticamente
                </p>
              </div>

              <GlassCard variant="success" className="xml-card-refined">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="xml-icon-refined">
                      <FileText className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="sf-pro-display font-bold text-slate-900 text-sm">
                        SNGPC_{receitaValidada?.prescriptionId}.xml
                      </h4>
                      <p className="sf-pro-text text-slate-600 text-xs">
                        Pronto para envio ao portal ANVISA
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleDownloadXmlAtual}
                    className="download-xml-button-refined"
                  >
                    <Download className="w-4 h-4 text-emerald-600" />
                  </button>
                </div>
              </GlassCard>

              <div className="flex justify-center pt-4">
                <button
                  onClick={novaDispensacao}
                  className="button-primary-refined"
                >
                  <QrCode className="w-4 h-4" />
                  <span>Nova Dispensação</span>
                </button>
              </div>
            </div>
          )}
        </GlassCard>

        <GlassCard elevated className="p-5 sm:p-6 shadow-2xl">
          <div className="flex items-center gap-3 mb-5">
            <div className="compliance-icon-refined">
              <Shield className="w-5 h-5 text-slate-700" />
            </div>
            <h3 className="sf-pro-display font-bold text-slate-900 text-lg">Compliance & Segurança</h3>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {[
              { icon: CheckCircle, text: 'Validação ICP-Brasil automática' },
              { icon: CheckCircle, text: 'XML SNGPC padrão ANVISA 2025' },
              { icon: CheckCircle, text: 'Dados criptografados (LGPD)' },
              { icon: CheckCircle, text: 'Portaria 344/98 • RDC 22/2014' }
            ].map((item, index) => (
              <div key={index} className="compliance-item-refined group">
                <div className="compliance-item-icon-refined">
                  <item.icon className="w-3.5 h-3.5 text-emerald-600" />
                </div>
                <span className="sf-pro-text text-slate-700 text-xs font-medium">{item.text}</span>
              </div>
            ))}
          </div>

          {/* ✅ ATUALIZADO: Botão diferente para Partner vs Basic */}
          <div className="flex justify-center">
            {isPartner ? (
              // ✅ Partner: Botão Gerenciar Catálogo
              <Link to="/catalogo-produtos" className="inventory-link-refined group">
                <div className="inventory-icon-refined bg-gradient-to-br from-purple-100 to-purple-200 border-purple-200/50">
                  <Package className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1 text-center">
                  <h4 className="sf-pro-display font-bold text-slate-900 text-sm">Gerenciar Catálogo</h4>
                  <p className="sf-pro-text text-slate-600 text-xs">Seus produtos para médicos</p>
                </div>
                <ChevronRight className="w-5 h-5 text-purple-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-all" />
              </Link>
            ) : (
              // ✅ Basic: Botão Upgrade Partner
              <Link to="/assinatura?upgrade=partner" className="upgrade-button-refined">
                <Sparkles className="w-4 h-4" />
                <span>Seja Pharma Partner</span>
                <ChevronRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </GlassCard>
      </main>

      <ModalHistoricoDispensacoes
        aberto={modalHistoricoAberto}
        onFechar={() => setModalHistoricoAberto(false)}
        dispensacoes={dispensacoesRecentes}
        carregando={historicoLoading}
        onCarregarMais={loadMaisHistorico}
        onDownloadXml={handleDownloadXmlHistorico}
        onRefresh={() => loadHistorico(true)}
        acabou={historicoAcabou}
      />

      {mensagem && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm">
          <div className={`notification-refined ${
            messageType === 'success' ? 'notification-success-refined' 
            : messageType === 'error' ? 'notification-error-refined'
            : messageType === 'warning' ? 'notification-warning-refined'
            : 'notification-info-refined'
          }`}>
            <div className="flex items-center gap-3">
              <div className="notification-icon-refined">
                {messageType === 'success' && <CheckCircle className="w-4 h-4 text-emerald-600" />}
                {messageType === 'error' && <AlertTriangle className="w-4 h-4 text-red-600" />}
                {messageType === 'warning' && <AlertTriangle className="w-4 h-4 text-amber-600" />}
                {messageType === 'info' && <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />}
              </div>
              <span className="sf-pro-text font-medium text-slate-900 text-xs flex-1">{mensagem}</span>
              <button onClick={() => setMensagem('')} className="notification-close-refined">
                <X className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" />
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="footer-refined">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
            <img src="/logo.PNG" alt="Scriptus" className="h-3.5 w-auto opacity-60" />
            <span className="text-[10px] sf-pro-text text-slate-500">
              Scriptus Tecnologia em Saúde LTDA © 62.420.408/0001-70
            </span>
          </div>
        </div>
      </footer>

      <style>{`
        /* ✅ Estilos do Contador Inline */
        .contador-uso-inline {
          padding: 8px 12px;
          border-radius: 10px;
          border: 1px solid;
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(8px);
        }

        .contador-inline-partner {
          border-color: rgba(168, 85, 247, 0.3);
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(243, 232, 255, 0.8));
        }

        .contador-inline-normal {
          border-color: rgba(16, 185, 129, 0.2);
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(236, 253, 245, 0.8));
        }

        .contador-inline-alerta {
          border-color: rgba(245, 158, 11, 0.3);
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 251, 235, 0.8));
        }

        .contador-inline-limite {
          border-color: rgba(239, 68, 68, 0.3);
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(254, 242, 242, 0.8));
        }

        .contador-inline-icon {
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          flex-shrink: 0;
        }

        .icon-container-premium {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.6));
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.8);
        }

        .input-glass-premium {
          background: rgba(255, 255, 255, 0.8);
          border: 1.5px solid rgba(255, 255, 255, 0.5);
          border-radius: 14px;
          backdrop-filter: blur(12px);
          transition: all 0.2s ease;
          color: #1f2937;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8);
        }
        .input-glass-premium::placeholder { 
          color: #9ca3af;
          font-size: 12px;
        }
        .input-glass-premium:focus {
          outline: none;
          background: rgba(255, 255, 255, 0.95);
          border-color: rgba(16, 185, 129, 0.5);
          box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.1), 0 4px 16px rgba(16, 185, 129, 0.1);
          transform: translateY(-1px);
        }
        .input-glass-premium:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          background: rgba(243, 244, 246, 0.8);
        }

        .button-primary-premium {
          padding: 12px 20px;
          border-radius: 14px;
          font-weight: 600;
          border: none;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          box-shadow: 0 8px 24px rgba(16, 185, 129, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2);
        }
        .button-primary-premium:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(16, 185, 129, 0.3);
        }
        .button-primary-premium:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .glass-badge-premium {
          display: inline-flex;
          align-items: center;
          padding: 3px 8px;
          background: rgba(16, 185, 129, 0.1);
          border: 1px solid rgba(16, 185, 129, 0.2);
          border-radius: 6px;
          color: #059669;
        }

        .button-glass-premium {
          padding: 10px 16px;
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(12px);
          border: 1.5px solid rgba(255, 255, 255, 0.5);
          border-radius: 12px;
          font-weight: 500;
          color: #4b5563;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8);
        }
        .button-glass-premium:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.95);
          color: #1f2937;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
        }

        .sf-pro-display {
          font-family: -apple-system, "SF Pro Display", BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        .sf-pro-text {
          font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        .bg-gradient-refined {
          background: linear-gradient(135deg, 
            #f8fafc 0%, 
            #ffffff 25%, 
            #f1f5f9 50%, 
            #ffffff 75%, 
            #ecfdf5 100%);
        }

        .background-pattern-refined {
          background-image: 
            radial-gradient(circle at 25% 25%, rgba(16, 185, 129, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, rgba(20, 184, 166, 0.06) 0%, transparent 50%),
            radial-gradient(circle at 50% 10%, rgba(99, 102, 241, 0.04) 0%, transparent 60%),
            radial-gradient(circle at 10% 90%, rgba(168, 85, 247, 0.03) 0%, transparent 50%);
          opacity: 0.7;
        }

        .background-glow-1 {
          position: absolute;
          top: 10%;
          left: 15%;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(16, 185, 129, 0.08) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(40px);
        }

        .background-glow-2 {
          position: absolute;
          bottom: 15%;
          right: 20%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(20, 184, 166, 0.06) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(35px);
        }

        .background-glow-3 {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(99, 102, 241, 0.02) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(60px);
        }

        .glass-card-refined {
          background: rgba(255, 255, 255, 0.80);
          backdrop-filter: blur(24px) saturate(180%);
          -webkit-backdrop-filter: blur(24px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.06),
            0 4px 16px rgba(0, 0, 0, 0.04),
            inset 0 1px 0 rgba(255, 255, 255, 0.9),
            inset 0 -1px 0 rgba(255, 255, 255, 0.5);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .glass-card-elevated-refined {
          background: rgba(255, 255, 255, 0.90);
          backdrop-filter: blur(30px) saturate(200%);
          -webkit-backdrop-filter: blur(30px) saturate(200%);
          border: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow: 
            0 25px 50px rgba(0, 0, 0, 0.08),
            0 12px 24px rgba(0, 0, 0, 0.06),
            0 6px 12px rgba(0, 0, 0, 0.04),
            inset 0 2px 0 rgba(255, 255, 255, 0.95),
            inset 0 -1px 0 rgba(255, 255, 255, 0.6);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .glass-card-highlight {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow: 
            0 12px 24px rgba(0, 0, 0, 0.06),
            0 6px 12px rgba(0, 0, 0, 0.04),
            inset 0 1px 0 rgba(255, 255, 255, 0.9);
          transition: all 0.3s ease;
        }

        .glass-card-info {
          background: rgba(239, 246, 255, 0.80);
          backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid rgba(147, 197, 253, 0.3);
          box-shadow: 
            0 8px 24px rgba(59, 130, 246, 0.08),
            0 4px 12px rgba(59, 130, 246, 0.04),
            inset 0 1px 0 rgba(255, 255, 255, 0.8);
        }

        .glass-card-success {
          background: rgba(240, 253, 244, 0.80);
          backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid rgba(167, 243, 208, 0.4);
          box-shadow: 
            0 8px 24px rgba(16, 185, 129, 0.08),
            0 4px 12px rgba(16, 185, 129, 0.04),
            inset 0 1px 0 rgba(255, 255, 255, 0.8);
        }

        .glass-card-warning {
          background: rgba(255, 251, 235, 0.80);
          backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid rgba(252, 211, 77, 0.4);
          box-shadow: 
            0 8px 24px rgba(245, 158, 11, 0.08),
            0 4px 12px rgba(245, 158, 11, 0.04),
            inset 0 1px 0 rgba(255, 255, 255, 0.8);
        }

        .header-refined {
          background: rgba(255, 255, 255, 0.75);
          backdrop-filter: blur(24px) saturate(180%);
          -webkit-backdrop-filter: blur(24px) saturate(180%);
          border-bottom: 1px solid rgba(255, 255, 255, 0.4);
        }

        .header-scrolled-refined {
          background: rgba(255, 255, 255, 0.90);
          backdrop-filter: blur(30px) saturate(200%);
          -webkit-backdrop-filter: blur(30px) saturate(200%);
          border-bottom: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
        }

        .logo-link-refined {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .logo-link-refined:hover {
          opacity: 0.8;
          transform: scale(1.05);
        }

        .card-title-refined {
          font-size: 1rem;
          font-weight: 700;
          color: #1e293b;
          letter-spacing: -0.025em;
          line-height: 1.3;
        }

        .main-icon-refined {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.6));
          backdrop-filter: blur(16px);
          border: 2px solid rgba(255, 255, 255, 0.6);
          border-radius: 16px;
          box-shadow: 
            0 8px 24px rgba(0, 0, 0, 0.08),
            0 4px 12px rgba(0, 0, 0, 0.04),
            inset 0 2px 0 rgba(255, 255, 255, 0.9);
          transition: all 0.3s ease;
        }

        .status-icon-refined {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          box-shadow: 
            0 6px 16px rgba(0, 0, 0, 0.1),
            0 3px 8px rgba(0, 0, 0, 0.06);
        }

        .info-card-icon-refined {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
          box-shadow: 
            0 4px 12px rgba(0, 0, 0, 0.1),
            0 2px 6px rgba(0, 0, 0, 0.06);
        }

        .section-icon-refined {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 9px;
          border: 1px solid rgba(16, 185, 129, 0.2);
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.08);
        }

        .compliance-icon-refined {
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6));
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 
            0 6px 16px rgba(0, 0, 0, 0.06),
            inset 0 1px 0 rgba(255, 255, 255, 0.8);
        }

        .info-icon-refined {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 9px;
          border: 1px solid rgba(59, 130, 246, 0.2);
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.08);
          flex-shrink: 0;
        }

        .badge-icp-refined {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(16, 185, 129, 0.08));
          border: 1px solid rgba(16, 185, 129, 0.25);
          border-radius: 8px;
          color: #059669;
          font-size: 0.7rem;
          font-weight: 600;
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.1);
        }

        .badge-anvisa-refined {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(59, 130, 246, 0.08));
          border: 1px solid rgba(59, 130, 246, 0.25);
          border-radius: 8px;
          color: #2563eb;
          font-size: 0.7rem;
          font-weight: 600;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
        }

        .status-badge-refined {
          padding: 6px 12px;
          border-radius: 10px;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.025em;
        }

        .status-badge-success {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.1));
          color: #065f46;
          border: 1px solid rgba(16, 185, 129, 0.3);
        }

        .status-badge-warning {
          background: linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(245, 158, 11, 0.1));
          color: #92400e;
          border: 1px solid rgba(245, 158, 11, 0.3);
        }

        .medicamento-badge {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          padding: 3px 7px;
          border-radius: 6px;
          font-size: 0.65rem;
          font-weight: 600;
          border: 1px solid;
        }

        .medicamento-badge-primary {
          background: rgba(99, 102, 241, 0.1);
          color: #4338ca;
          border-color: rgba(99, 102, 241, 0.2);
        }

        .medicamento-badge-info {
          background: rgba(59, 130, 246, 0.1);
          color: #1d4ed8;
          border-color: rgba(59, 130, 246, 0.2);
        }

        .medicamento-badge-success {
          background: rgba(16, 185, 129, 0.1);
          color: #047857;
          border-color: rgba(16, 185, 129, 0.2);
        }

        .medicamento-badge-warning {
          background: rgba(245, 158, 11, 0.1);
          color: #d97706;
          border-color: rgba(245, 158, 11, 0.2);
        }

        .input-glass-refined {
          padding: 12px 14px;
          background: rgba(255, 255, 255, 0.85);
          border: 2px solid rgba(255, 255, 255, 0.6);
          border-radius: 12px;
          backdrop-filter: blur(16px);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          font-size: 0.8rem;
          color: #1e293b;
          box-shadow: 
            0 4px 12px rgba(0, 0, 0, 0.04),
            inset 0 1px 0 rgba(255, 255, 255, 0.8);
        }
        .input-glass-refined::placeholder { 
          color: #94a3b8; 
        }
        .input-glass-refined:focus {
          outline: none;
          background: rgba(255, 255, 255, 0.95);
          border-color: rgba(16, 185, 129, 0.6);
          box-shadow: 
            0 0 0 4px rgba(16, 185, 129, 0.1),
            0 8px 24px rgba(16, 185, 129, 0.1),
            0 4px 12px rgba(0, 0, 0, 0.06);
          transform: translateY(-1px);
        }

        .button-primary-refined {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 14px 20px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border: none;
          border-radius: 14px;
          color: white;
          font-weight: 700;
          font-size: 0.8rem;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 
            0 10px 32px rgba(16, 185, 129, 0.25),
            0 6px 16px rgba(16, 185, 129, 0.15),
            inset 0 2px 0 rgba(255, 255, 255, 0.25);
        }
        .button-primary-refined:hover:not(:disabled) {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          transform: translateY(-2px) scale(1.02);
          box-shadow: 
            0 16px 40px rgba(16, 185, 129, 0.3),
            0 8px 20px rgba(16, 185, 129, 0.2);
        }
        .button-primary-refined:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
          background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
          box-shadow: 
            0 4px 12px rgba(0, 0, 0, 0.1),
            inset 0 2px 0 rgba(255, 255, 255, 0.1);
        }

        .button-secondary-refined {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 10px 16px;
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(16px);
          border: 2px solid rgba(255, 255, 255, 0.6);
          border-radius: 12px;
          color: #475569;
          font-weight: 600;
          font-size: 0.8rem;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 
            0 6px 16px rgba(0, 0, 0, 0.04),
            inset 0 1px 0 rgba(255, 255, 255, 0.8);
        }
        .button-secondary-refined:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.95);
          color: #1e293b;
          transform: translateY(-1px);
          box-shadow: 
            0 10px 24px rgba(0, 0, 0, 0.08),
            inset 0 1px 0 rgba(255, 255, 255, 0.9);
        }

        .upgrade-button-refined {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 24px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-radius: 14px;
          color: white;
          font-weight: 700;
          font-size: 0.8rem;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 
            0 10px 32px rgba(16, 185, 129, 0.25),
            inset 0 2px 0 rgba(255, 255, 255, 0.25);
        }
        .upgrade-button-refined:hover {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          transform: translateY(-2px);
          box-shadow: 
            0 16px 40px rgba(16, 185, 129, 0.3);
        }

        .medicamento-card-refined {
          padding: 20px;
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.6);
          border-radius: 18px;
          box-shadow: 
            0 12px 24px rgba(0, 0, 0, 0.05),
            0 6px 12px rgba(0, 0, 0, 0.03),
            inset 0 2px 0 rgba(255, 255, 255, 0.9);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .medicamento-card-refined:hover {
          transform: translateY(-2px) scale(1.01);
          box-shadow: 
            0 20px 40px rgba(0, 0, 0, 0.08),
            0 12px 24px rgba(0, 0, 0, 0.05);
          border-color: rgba(16, 185, 129, 0.3);
        }

        .medicamento-header-refined {
          position: relative;
        }
        .medicamento-header-refined::after {
          content: '';
          position: absolute;
          bottom: -14px;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, 
            transparent 0%, 
            rgba(16, 185, 129, 0.3) 50%, 
            transparent 100%);
        }

        .medicamento-icon-refined {
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.1));
          border: 2px solid rgba(16, 185, 129, 0.2);
          border-radius: 14px;
          box-shadow: 
            0 6px 16px rgba(16, 185, 129, 0.1),
            inset 0 2px 0 rgba(255, 255, 255, 0.6);
          transition: all 0.3s ease;
        }

        .progress-container-refined {
          width: 100%;
          height: 5px;
          background: rgba(226, 232, 240, 0.8);
          border-radius: 5px;
          overflow: hidden;
          position: relative;
          box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        .progress-bar-refined {
          height: 100%;
          border-radius: 5px;
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }
        .progress-bar-refined::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, 
            transparent, 
            rgba(255, 255, 255, 0.4), 
            transparent);
          animation: shimmer 2s infinite;
        }

        .progress-bar-success {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
        }

        .progress-bar-warning {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
        }

        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }

        .status-item-refined {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 10px;
          border-radius: 8px;
          font-size: 0.7rem;
          font-weight: 600;
          border: 1px solid;
        }

        .status-item-success {
          background: rgba(16, 185, 129, 0.1);
          color: #047857;
          border-color: rgba(16, 185, 129, 0.25);
        }

        .status-item-warning {
          background: rgba(245, 158, 11, 0.1);
          color: #d97706;
          border-color: rgba(245, 158, 11, 0.25);
        }

        .status-item-link {
          background: rgba(99, 102, 241, 0.1);
          color: #4338ca;
          border-color: rgba(99, 102, 241, 0.25);
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .status-item-link:hover {
          background: rgba(99, 102, 241, 0.2);
          transform: translateX(2px);
        }

        .info-card-refined {
          padding: 18px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .info-card-refined:hover {
          transform: translateY(-2px);
          box-shadow: 
            0 16px 32px rgba(0, 0, 0, 0.08),
            0 8px 16px rgba(0, 0, 0, 0.06);
        }

        .info-card-header-refined {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 14px;
        }

        .info-card-label-refined {
          font-size: 0.65rem;
          font-weight: 700;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .info-card-content-refined {
          space: 6px;
        }

        .info-card-title-refined {
          font-size: 0.95rem;
          font-weight: 700;
          color: #1e293b;
          line-height: 1.3;
          margin-bottom: 3px;
        }

        .info-card-subtitle-refined {
          font-size: 0.8rem;
          font-weight: 500;
          color: #64748b;
          margin-bottom: 3px;
        }

        .info-card-extra-refined {
          font-size: 0.7rem;
          color: #94a3b8;
        }

        .section-header-refined {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .section-title-refined {
          font-size: 1.05rem;
          font-weight: 700;
          color: #1e293b;
          line-height: 1.3;
          margin-bottom: 3px;
        }

        .section-subtitle-refined {
          font-size: 0.75rem;
          color: #64748b;
          font-weight: 500;
        }

        .loading-container-refined {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          gap: 20px;
        }

        .loading-animation-refined {
          position: relative;
        }

        .loading-glow-refined {
          position: absolute;
          inset: -18px;
          background: rgba(16, 185, 129, 0.15);
          border-radius: 50%;
          filter: blur(22px);
        }

        .loading-icon-refined {
          position: relative;
          width: 64px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-radius: 50%;
          box-shadow: 
            0 12px 32px rgba(16, 185, 129, 0.3),
            0 6px 16px rgba(16, 185, 129, 0.2);
        }

        .success-container-refined {
          text-align: center;
          padding: 20px 0;
          space: 28px;
        }

        .success-animation-refined {
          position: relative;
          display: inline-block;
          margin-bottom: 28px;
        }

        .success-glow-refined {
          position: absolute;
          inset: -20px;
          background: rgba(16, 185, 129, 0.2);
          border-radius: 50%;
          filter: blur(28px);
        }

        .success-icon-refined {
          position: relative;
          width: 72px;
          height: 72px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-radius: 50%;
          box-shadow: 
            0 16px 40px rgba(16, 185, 129, 0.3),
            0 8px 20px rgba(16, 185, 129, 0.2);
        }

        .success-title-refined {
          font-size: 1.4rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 6px;
        }

        .success-subtitle-refined {
          font-size: 0.85rem;
          color: #64748b;
          font-weight: 500;
        }

        .xml-card-refined {
          padding: 18px;
          max-width: 26rem;
          margin: 0 auto 28px;
        }

        .xml-icon-refined {
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 10px;
          border: 1px solid rgba(16, 185, 129, 0.2);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.1);
        }

        .download-xml-button-refined {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.8);
          border: 1px solid rgba(16, 185, 129, 0.2);
          border-radius: 10px;
          transition: all 0.3s ease;
        }
        .download-xml-button-refined:hover {
          background: rgba(16, 185, 129, 0.1);
          border-color: rgba(16, 185, 129, 0.3);
        }

        .patient-summary-refined {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 18px;
          background: rgba(248, 250, 252, 0.8);
          border: 1px solid rgba(226, 232, 240, 0.6);
          border-radius: 12px;
          backdrop-filter: blur(8px);
        }

        .compliance-item-refined {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          background: rgba(255, 255, 255, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.6);
          border-radius: 10px;
          transition: all 0.3s ease;
        }
        .compliance-item-refined:hover {
          background: rgba(255, 255, 255, 0.8);
          transform: translateY(-1px);
        }

        .compliance-item-icon-refined {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 7px;
          border: 1px solid rgba(16, 185, 129, 0.2);
          transition: all 0.3s ease;
        }
        .compliance-item-refined:hover .compliance-item-icon-refined {
          background: rgba(16, 185, 129, 0.1);
          border-color: rgba(16, 185, 129, 0.3);
        }

        .inventory-link-refined {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 18px 20px;
          background: rgba(255, 255, 255, 0.6);
          border: 1px solid rgba(255, 255, 255, 0.5);
          border-radius: 14px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          backdrop-filter: blur(12px);
          width: 100%;
          max-width: 26rem;
        }
        .inventory-link-refined:hover {
          background: rgba(255, 255, 255, 0.85);
          transform: translateY(-2px);
          box-shadow: 
            0 12px 32px rgba(0, 0, 0, 0.08),
            0 6px 16px rgba(0, 0, 0, 0.06);
        }

        .inventory-icon-refined {
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
          transition: all 0.3s ease;
        }

        .notification-refined {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(24px) saturate(200%);
          border: 1px solid rgba(255, 255, 255, 0.6);
          border-radius: 14px;
          padding: 14px 18px;
          box-shadow: 
            0 20px 60px rgba(0, 0, 0, 0.12),
            0 10px 30px rgba(0, 0, 0, 0.08);
          animation: slideInUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .notification-success-refined {
          border-color: rgba(16, 185, 129, 0.3);
          box-shadow: 
            0 20px 60px rgba(16, 185, 129, 0.15),
            0 10px 30px rgba(16, 185, 129, 0.1);
        }

        .notification-error-refined {
          border-color: rgba(239, 68, 68, 0.3);
          box-shadow: 
            0 20px 60px rgba(239, 68, 68, 0.15),
            0 10px 30px rgba(239, 68, 68, 0.1);
        }

        .notification-warning-refined {
          border-color: rgba(245, 158, 11, 0.3);
          box-shadow: 
            0 20px 60px rgba(245, 158, 11, 0.15),
            0 10px 30px rgba(245, 158, 11, 0.1);
        }

        .notification-info-refined {
          border-color: rgba(59, 130, 246, 0.3);
          box-shadow: 
            0 20px 60px rgba(59, 130, 246, 0.15),
            0 10px 30px rgba(59, 130, 246, 0.1);
        }

        .notification-icon-refined {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 9px;
          border: 1px solid rgba(255, 255, 255, 0.6);
        }

        .notification-close-refined {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.6);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 7px;
          transition: all 0.3s ease;
        }
        .notification-close-refined:hover {
          background: rgba(255, 255, 255, 0.9);
        }

        @keyframes slideInUp {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .footer-refined {
          background: rgba(255, 255, 255, 0.75);
          backdrop-filter: blur(20px);
          border-top: 1px solid rgba(255, 255, 255, 0.5);
          padding: 16px 0;
          margin-top: 56px;
        }

        @media (max-width: 768px) {
          .medicamento-card-refined {
            padding: 18px;
          }
          
          .medicamento-icon-refined {
            width: 40px;
            height: 40px;
          }
          
          .input-glass-premium,
          .input-glass-refined {
            font-size: 16px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            transition-duration: 0.01ms !important;
          }
        }

        @media (prefers-contrast: high) {
          .glass-card-refined, 
          .glass-card-elevated-refined {
            background: rgba(255, 255, 255, 0.95);
            border: 2px solid rgba(0, 0, 0, 0.2);
          }
        }

        @media (pointer: coarse) {
          button, a, [role="button"] { 
            min-height: 44px; 
            min-width: 44px; 
          }
        }
      `}</style>
    </div>
  );
}